'use client'

import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import axiosInstance from '@/_lib/axiosInstance'
import { ISearchResponse } from '@/_types/search'
import { sendSearchEvent } from '@/_utils/analytics.utils'

interface UseSearchParams {
	search: string
	perPage?: number
	page?: number
	enabled?: boolean
	debounceMs?: number
}

async function fetchSearch({
	search,
	perPage = 10,
	page = 1
}: {
	search: string
	perPage: number
	page: number
}): Promise<ISearchResponse> {
	const response = await axiosInstance.get<ISearchResponse>('/v1/web/webContent/search', {
		params: {
			search,
			perPage,
			page
		}
	})
	return response.data
}

export function useSearch({
	search,
	perPage = 10,
	page = 1,
	enabled = true,
	debounceMs = 300
}: UseSearchParams) {
	const [debouncedSearch, setDebouncedSearch] = useState(search)
	const lastSearchEventSent = useRef<string>('')

	// Debounce search input
	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedSearch(search)
		}, debounceMs)

		return () => {
			clearTimeout(handler)
		}
	}, [search, debounceMs])

	const shouldFetch = enabled && debouncedSearch.trim().length > 0

	const query = useQuery({
		queryKey: ['search', debouncedSearch, perPage, page],
		queryFn: async () => {
			const result = await fetchSearch({
				search: debouncedSearch,
				perPage,
				page
			})
			return result
		},
		enabled: shouldFetch,
		retry: 1
	})

	// Send GA event when search is successful and search term has changed
	useEffect(() => {
		if (
			query.isSuccess &&
			debouncedSearch.trim().length > 0 &&
			lastSearchEventSent.current !== debouncedSearch
		) {
			sendSearchEvent(debouncedSearch)
			lastSearchEventSent.current = debouncedSearch
		}
	}, [query.isSuccess, debouncedSearch])

	const clearSearch = useCallback(() => {
		setDebouncedSearch('')
		lastSearchEventSent.current = ''
	}, [])

	return {
		...query,
		clearSearch,
		isSearching: search !== debouncedSearch,
		hasSearchTerm: debouncedSearch.trim().length > 0
	}
}
